import { Upload, X } from 'lucide-react'
import { memo, useRef, type FC } from 'react'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

import { EditableChecklistField } from './EditableChecklistField'
import { FieldHelpPopover, FieldShell, type FieldRendererProps, type FormField } from './FieldShell'
import { CHECKLIST_TYPE } from '../lib/checklistHelper'
import type { DynamicField, GenericDynamicField } from '../types'
import { DateSinglePicker } from '@components/ui/DateSinglePicker'
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  Input,
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
  CheckboxBlock,
  Label,
  MultiSelect,
  SingleSelect,
  Separator,
} from '@fp/ui-kit'
import { splitAndCapitalizeEachWord } from '@lib/textFormatters'
import { dateOnlyToPickerDate, pickerDateToDateOnly, toDateOnly } from '@lib/tz'
import { Calendar } from '@ui/Calendar'

export type MarkdownFieldProps = { paramsField: { value?: string } }

export const MarkdownField: FC<MarkdownFieldProps> = memo(({ paramsField }) => (
  <div className='markdown'>
    <Markdown remarkPlugins={[remarkGfm]}>{`${paramsField.value || '-'}`}</Markdown>
  </div>
))

/** Value is optional: without it a bare divider, with it the text sits in the middle of the divider. */
export const DividerField: FC<{ paramsField: { value?: string } }> = memo(({ paramsField }) => {
  const value = typeof paramsField.value === 'string' ? paramsField.value : ''
  const Divider = <Separator className='flex flex-1 m-0 min-w-8' />

  return (
    <div className='w-full relative flex items-center py-1'>
      {Divider}
      {value && <div className='text-center px-2 mx-2 w-fit'>{value}</div>}
      {Divider}
    </div>
  )
})

export type FieldTypes = DynamicField['type']

export { FieldShell, type FieldRendererProps }

export const FieldRenderer: FC<FieldRendererProps> = memo(({ paramsField, field, error, helperText }) => {
  const fieldId = paramsField.param_key ? `dynamic-field-${paramsField.param_key}` : undefined
  const inputValue = field.value ?? ''
  const shellProps = { paramsField, error, helperText }

  switch (paramsField.type) {
    case 'markdown':
      return <MarkdownField paramsField={paramsField} />

    case 'divider':
      return <DividerField paramsField={paramsField} />

    case 'selector':
      return (
        <FieldShell {...shellProps}>
          {paramsField.multiple ? (
            <MultiSelector paramsField={paramsField} field={field} />
          ) : paramsField.searchable ? (
            <SearchableSelect paramsField={paramsField} field={field} />
          ) : (
            <PlainSelect paramsField={paramsField} field={field} />
          )}
        </FieldShell>
      )

    case CHECKLIST_TYPE:
      return <EditableChecklistField paramsField={paramsField} field={field} error={error} helperText={helperText} />

    case 'checkbox': {
      const checkboxId = fieldId ? `${fieldId}-checkbox` : undefined

      return (
        <div className='flex items-center gap-2 pb-4'>
          <CheckboxBlock id={checkboxId} checked={!!field.value} onCheckedChange={(c) => field.onChange(!!c)} />
          <Label htmlFor={checkboxId}>{paramsField.placeholder || paramsField.name}</Label>
          <FieldHelpPopover fieldName={paramsField.name} helperText={helperText} />
        </div>
      )
    }

    case 'number':
      return (
        <FieldShell {...shellProps}>
          <Input
            type='number'
            placeholder={paramsField.placeholder}
            disabled={paramsField.disabled}
            {...field}
            value={inputValue}
          />
        </FieldShell>
      )

    case 'file':
      return isFileUploadField(paramsField) ? (
        <FieldShell {...shellProps}>
          <FileUploadField paramsField={paramsField} field={field} />
        </FieldShell>
      ) : (
        <FileLinkField field={paramsField} />
      )

    // A `date` field is a bare `YYYY-MM-DD` on the wire; `datetime` below carries the moment.
    case 'date': {
      const dateOnly = toDateOnly(field.value as string)

      return (
        <FieldShell {...shellProps}>
          <Popover>
            <PopoverTrigger asChild>
              <Input
                readOnly
                value={dateOnly || (paramsField.placeholder ?? 'Pick a date')}
                className='cursor-pointer'
              />
            </PopoverTrigger>
            <PopoverContent className='p-0'>
              <Calendar
                mode='single'
                disableFutureDates={false}
                selected={dateOnlyToPickerDate(dateOnly)}
                onSelect={(d) => field.onChange(d ? pickerDateToDateOnly(d) : '')}
              />
            </PopoverContent>
          </Popover>
        </FieldShell>
      )
    }

    case 'datetime':
      return (
        <FieldShell {...shellProps}>
          <DateSinglePicker
            value={(field.value as string | null | undefined) ?? undefined}
            disableFutureDates={false}
            onChange={(iso) => field.onChange(iso ?? undefined)}
            isFullWidth={true}
            showTime={true}
          />
        </FieldShell>
      )

    default:
      return (
        <FieldShell {...shellProps}>
          <Input
            type='text'
            placeholder={paramsField.placeholder}
            disabled={paramsField.disabled}
            {...field}
            value={inputValue}
          />
        </FieldShell>
      )
  }
})

type PlainSelectProps = {
  paramsField: GenericDynamicField
  field: FormField
}
const PlainSelect: FC<PlainSelectProps> = memo(({ paramsField, field }) => (
  <Select value={String(field.value)} onValueChange={field.onChange} disabled={paramsField.disabled}>
    <SelectTrigger>
      <SelectValue placeholder={paramsField.placeholder ?? 'Select…'} />
    </SelectTrigger>
    <SelectContent>
      {paramsField.options?.length ? (
        paramsField.options.map((o) => (
          <SelectItem key={o.value} value={String(o.value)}>
            {o.label}
          </SelectItem>
        ))
      ) : (
        <div className='py-6 text-center text-sm text-muted-foreground'>No options available.</div>
      )}
    </SelectContent>
  </Select>
))

type MultiSelectorProps = {
  paramsField: GenericDynamicField
  field: FormField
}
/** `paramsField.searchable` is moot here — `MultiSelect` always filters its options list inline. */
const MultiSelector: FC<MultiSelectorProps> = memo(({ paramsField, field }) => (
  <MultiSelect
    options={typeof paramsField.options !== 'undefined' ? paramsField.options : []}
    value={Array.isArray(field.value) ? (field.value as string[]) : []}
    onValueChange={field.onChange}
    placeholder={paramsField.placeholder ?? `Select ${paramsField.name}`}
    disabled={paramsField.disabled}
    modal
  />
))

type SearchableSelectProps = {
  paramsField: GenericDynamicField
  field: FormField
}
const SearchableSelect: FC<SearchableSelectProps> = memo(({ paramsField, field }) => (
  <SingleSelect
    {...field}
    options={typeof paramsField.options !== 'undefined' ? paramsField.options : []}
    value={field.value || ''}
    placeholder={`Select ${paramsField.name}`}
    disabled={paramsField.disabled}
    modal
  />
))

/**
 * A `file` field is an upload when the backend lists the MIME types it accepts; without `accept`
 * it is the legacy read-only variant that only links to an already stored document.
 */
const isFileUploadField = (paramsField: DynamicField): paramsField is GenericDynamicField =>
  'accept' in paramsField && !!paramsField.accept?.length

const FileLinkField = ({ field }: { field: Partial<GenericDynamicField> }) => {
  return (
    <div className='w-full border border-muted border-b-3 border-b-primary p-2  hover:text-primary rounded'>
      <a href={field.default as string} target='_blank' rel='noopener noreferrer' className='w-full block'>
        {splitAndCapitalizeEachWord(field.name || '-')}
      </a>
    </div>
  )
}

type FileUploadFieldProps = {
  paramsField: GenericDynamicField
  field: FormField
}

const FileUploadField: FC<FileUploadFieldProps> = memo(({ paramsField, field }) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const inputId = `dynamic-file-${paramsField.param_key}`
  const file = field.value instanceof File ? field.value : null

  // The picked file lives in RHF state, so the native input is cleared too — otherwise re-picking
  // the same file after a reset fires no `change` event and the field stays empty.
  const clear = () => {
    if (inputRef.current) inputRef.current.value = ''
    field.onChange('')
  }

  return (
    <div className='flex items-center gap-2'>
      <label
        htmlFor={inputId}
        aria-disabled={paramsField.disabled}
        className={`inline-flex shrink-0 items-center gap-2 rounded border border-input px-3 py-2 text-sm ${
          paramsField.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-muted'
        }`}
      >
        <Upload className='size-4' />
        {file ? 'Replace file' : 'Choose file'}
      </label>

      <input
        ref={inputRef}
        id={inputId}
        type='file'
        className='sr-only'
        accept={paramsField.accept?.join(',')}
        disabled={paramsField.disabled}
        onChange={(e) => field.onChange(e.target.files?.[0] ?? '')}
      />

      <span className='min-w-0 flex-1 truncate text-sm text-muted-foreground' title={file?.name}>
        {file ? file.name : 'No file selected'}
      </span>

      {file && (
        <button
          type='button'
          aria-label={`Remove ${file.name}`}
          onClick={clear}
          className='shrink-0 text-muted-foreground hover:text-foreground'
        >
          <X className='size-4' />
        </button>
      )}
    </div>
  )
})
